import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import {
	FloatingInput,
	FloatingDropdown,
	MultiSelectDropdown,
	Snackbar,
} from '../../../components/ui';
import { ArrowLeft, DollarSign, Target, Building } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { ClientApiService } from '../../../services/clientApi';
import {
	useCountries,
	useStates,
	useCities,
	useLocationTypes,
	useImpactTypes,
	useBillingTypes,
	useBillingSubTypes,
} from '../../../hooks/useLocationData';
import TokenManager from '../../../utils/tokenManager';
import { FacilityDropdown } from '../../../components/FacilityDropdown';
import { MONTH_OPTIONS } from '../../../features/dashboard/config/constants';

/** Effective-from years: last year through 2 years out — covers back-dating + future rate changes. */
const EFFECTIVE_YEAR_OPTIONS = (() => {
	const current = new Date().getFullYear();
	return [current - 1, current, current + 1, current + 2].map(y => ({
		value: String(y),
		label: String(y),
	}));
})();

interface ClientFormData {
	// Basic Information
	name: string;
	address1: string;
	address2: string;
	zipcode: string;
	landmark: string;
	latitude: string;
	longitude: string;
	onSiteManpower: boolean;

	// Location
	locationType: string;
	country: string;
	state: string;
	city: string;

	// Billing Type
	billingType: string;
	fixedPrice?: string;
	/** 'YYYY-MM-01' — month the first Fixed Price for this new client kicks in. */
	fixedPriceEffectiveFrom?: string;
	billingSubType?: string;

	// Impact Type
	impactTypes: string[];

	// Facility (when onSiteManpower is true)
	facility?: string;
}

export const AddClient: React.FC = () => {
	const navigate = useNavigate();
	const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

	// Debug authentication
	useEffect(() => {
		console.log('AddClient Auth Debug:');
		console.log('- Is Authenticated:', isAuthenticated);
		console.log('- User:', user);
		console.log('- Token exists:', !!TokenManager.getBearerToken());
		console.log('- Token data:', TokenManager.getTokenData());
	}, [isAuthenticated, user]);

	// API hooks
	const { countries, loading: countriesLoading } = useCountries();
	const { states, loading: statesLoading } = useStates();
	const { cities, loading: citiesLoading } = useCities();
	const { locationTypes, loading: locationTypesLoading } = useLocationTypes();

	// Debug: Check location types data
	useEffect(() => {
		console.log('🔍 AddClient: Location Types:', locationTypes);
		console.log('🔍 AddClient: Location Types Loading:', locationTypesLoading);
	}, [locationTypes, locationTypesLoading]);
	const { impactTypes, loading: impactTypesLoading } = useImpactTypes();
	const { billingTypes, loading: billingTypesLoading } = useBillingTypes();

	// Generate random values for testing
	const generateRandomValues = () => {
		const randomNames = [
			'Test Client A',
			'Sample Restaurant B',
			'Demo Location C',
			'Quick Test D',
			'Mock Client E',
		];
		const randomAddresses = [
			'123 Main Street',
			'456 Business Ave',
			'789 Commercial Rd',
			'321 Office Plaza',
			'654 Corporate Blvd',
		];
		const randomLandmarks = [
			'Near Metro Station',
			'Opposite Mall',
			'Behind Hospital',
			'Next to School',
			'Near Airport',
		];
		const randomZipcodes = ['400001', '400002', '400003', '400004', '400005'];
		const randomLatitudes = ['19.0760', '19.0176', '19.2183', '19.1136', '19.0225'];
		const randomLongitudes = ['72.8777', '72.8562', '72.9781', '72.8697', '72.8546'];

		return {
			name: randomNames[Math.floor(Math.random() * randomNames.length)],
			address1: randomAddresses[Math.floor(Math.random() * randomAddresses.length)],
			address2: 'Suite ' + Math.floor(Math.random() * 100),
			zipcode: randomZipcodes[Math.floor(Math.random() * randomZipcodes.length)],
			landmark: randomLandmarks[Math.floor(Math.random() * randomLandmarks.length)],
			latitude: randomLatitudes[Math.floor(Math.random() * randomLatitudes.length)],
			longitude: randomLongitudes[Math.floor(Math.random() * randomLongitudes.length)],
		};
	};

	const randomValues = generateRandomValues();

	const [formData, setFormData] = useState<ClientFormData>({
		name: randomValues.name,
		address1: randomValues.address1,
		address2: randomValues.address2,
		zipcode: randomValues.zipcode,
		landmark: randomValues.landmark,
		latitude: randomValues.latitude,
		longitude: randomValues.longitude,
		onSiteManpower: false,
		locationType: '',
		country: '82', // India's ID from the API
		state: '',
		city: '',
		billingType: '',
		fixedPrice: '',
		// Default the effective-from to the first of the current month — most
		// new clients start billing this month. User can override before save.
		fixedPriceEffectiveFrom: (() => {
			const d = new Date();
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
		})(),
		billingSubType: '',
		impactTypes: [],
		facility: '',
	});

	// Billing sub types hook - must be called after formData is initialized
	const { billingSubTypes, loading: billingSubTypesLoading } = useBillingSubTypes(
		formData.billingType || undefined
	);

	const [errors, setErrors] = useState<Partial<Record<keyof ClientFormData, string>>>({});
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Snackbar state
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		type: 'success' as 'success' | 'error' | 'info',
	});

	// Set India as default country
	useEffect(() => {
		if (countries.length > 0 && !formData.country) {
			const indiaCountry = countries.find(country => country.label.toLowerCase().includes('india'));
			if (indiaCountry) {
				setFormData(prev => ({ ...prev, country: indiaCountry.value }));
			}
		}
	}, [countries, formData.country]);

	// Debug: Check localStorage data
	useEffect(() => {
		const storedUserData = localStorage.getItem('user_data');
		const parsedData = storedUserData ? JSON.parse(storedUserData) : null;
		console.log('💾 AddClient: Stored user data in localStorage:', parsedData);
		console.log('💾 AddClient: city_id in localStorage:', parsedData?.city_id);
		console.log('💾 AddClient: state_id in localStorage:', parsedData?.state_id);
		console.log('💾 AddClient: userTypeId in localStorage:', parsedData?.userTypeId);

		// Also check what's in Redux
		console.log('🔍 AddClient: Redux user object:', user);
		console.log('🔍 AddClient: Redux user.city_id:', user?.city_id);
		console.log('🔍 AddClient: Redux user.state_id:', user?.state_id);
		console.log('🔍 AddClient: Redux user.userTypeId:', user?.userTypeId);
	}, [user]);

	// Set user's city and state as default for non-super admins
	useEffect(() => {
		console.log('🔍 AddClient: Checking user data for preloading:', {
			user: user,
			city_id: user?.city_id,
			state_id: user?.state_id,
			userTypeId: user?.userTypeId,
			cities: cities.length,
			states: states.length,
		});

		// Check if user is not a super admin (userTypeId > 4) and has location data
		if (user?.userTypeId && user.userTypeId > 4 && user?.city_id && cities.length > 0) {
			console.log('✅ AddClient: User is non-super admin, preloading city and state');

			// Find and set the user's city
			const userCity = cities.find(city => city.value === user.city_id?.toString());
			console.log('🏙️ AddClient: Found user city:', userCity);
			if (userCity) {
				setFormData(prev => ({ ...prev, city: userCity.value }));
				console.log('✅ AddClient: City set to:', userCity.value);
			}

			// Find and set the user's state
			if (user.state_id && states.length > 0) {
				const userState = states.find(state => state.value === user.state_id?.toString());
				console.log('🏛️ AddClient: Found user state:', userState);
				if (userState) {
					setFormData(prev => ({ ...prev, state: userState.value }));
					console.log('✅ AddClient: State set to:', userState.value);
				}
			} else {
				console.log('⚠️ AddClient: No state_id in user data or states not loaded');
			}

			// Set country to India (82) for non-super admins
			setFormData(prev => ({ ...prev, country: '82' }));
			console.log('✅ AddClient: Country set to India (82)');
		} else {
			console.log('❌ AddClient: Conditions not met for preloading:', {
				hasUserTypeId: !!user?.userTypeId,
				isNonSuperAdmin: user?.userTypeId && user.userTypeId > 4,
				hasCityId: !!user?.city_id,
				hasCities: cities.length > 0,
			});
		}
	}, [user?.city_id, user?.state_id, user?.userTypeId, cities, states, user]);

	const handleInputChange = (field: keyof ClientFormData, value: string | boolean) => {
		setFormData(prev => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors(prev => ({ ...prev, [field]: undefined }));
		}

		// Clear facility when city changes (since facilities are city-specific)
		if (field === 'city') {
			setFormData(prev => ({ ...prev, facility: '' }));
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		console.log('🚀 AddClient: Submit button clicked!');

		// Validate form and get errors
		const newErrors: Partial<Record<keyof ClientFormData, string>> = {};

		// Required fields validation
		if (!formData.name.trim()) newErrors.name = 'Name is required';
		if (!formData.address1.trim()) newErrors.address1 = 'Address 1 is required';
		if (!formData.locationType) newErrors.locationType = 'Location Type is required';
		if (!formData.billingType) newErrors.billingType = 'Billing Type is required';
		if (!formData.impactTypes || formData.impactTypes.length === 0)
			newErrors.impactTypes = 'At least one Impact Type is required';
		if (!formData.country) newErrors.country = 'Country is required';
		if (!formData.state) newErrors.state = 'State is required';
		if (!formData.city) newErrors.city = 'City is required';
		if (!formData.facility || formData.facility === '') newErrors.facility = 'Facility is required';
		if (!formData.landmark.trim()) newErrors.landmark = 'Landmark is required';
		if (!formData.latitude.trim()) newErrors.latitude = 'Latitude is required';
		if (!formData.longitude.trim()) newErrors.longitude = 'Longitude is required';
		if (!formData.zipcode.trim()) newErrors.zipcode = 'Zipcode is required';

		// Conditional validation for Fixed Price
		if (formData.billingType === '3' && !formData.fixedPrice?.trim()) {
			newErrors.fixedPrice = 'Fixed Price is required';
		}

		// Conditional validation for Billing Sub Type
		if (formData.billingType === '4' && !formData.billingSubType) {
			newErrors.billingSubType = 'Billing Sub Type is required';
		}

		if (Object.keys(newErrors).length > 0) {
			console.log('❌ AddClient: Form validation failed');
			console.log('❌ AddClient: Validation errors:', newErrors);
			setErrors(newErrors);
			return;
		}

		console.log('✅ AddClient: Form validation passed');
		setIsSubmitting(true);
		try {
			// Prepare API payload matching the expected format
			const payload: any = {
				location: formData.name,
				address_1: formData.address1,
				address_2: formData.address2,
				landmark: formData.landmark,
				zipcode: formData.zipcode,
				latitude: formData.latitude,
				longitude: formData.longitude,
				city_id: parseInt(formData.city),
				state_id: parseInt(formData.state),
				country_id: parseInt(formData.country),
				location_type: parseInt(formData.locationType),
				billing_type_id: parseInt(formData.billingType),
				billing_sub_type_id: formData.billingSubType
					? parseInt(formData.billingSubType)
					: undefined,
				impact_type_ids: formData.impactTypes.map(id => parseInt(id)),
				onSiteManPower: formData.onSiteManpower ? 1 : 0,
				facility_id: parseInt(formData.facility!), // Always required now (validated before submission)
			};

			// Add optional fields based on billing type. effective_from anchors
			// the first history row so historical reports for past months
			// (before the client existed) never get a stray price applied.
			if (formData.billingType === '3' && formData.fixedPrice) {
				payload.fixed_price = formData.fixedPrice;
				payload.fixed_price_effective_from = formData.fixedPriceEffectiveFrom;
			}

			console.log('📝 Submitting client data:', payload);

			// Call actual API
			await ClientApiService.addClient(payload);

			// Show success message
			setSnackbar({
				open: true,
				message: 'Client created successfully!',
				type: 'success',
			});

			// Navigate after showing success message
			setTimeout(() => {
				navigate('/clients/manage');
			}, 800);
		} catch (error) {
			console.error('Failed to add client:', error);
			setSnackbar({
				open: true,
				message: 'Failed to create client. Please try again.',
				type: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className='min-h-screen bg-white p-4'>
			<div className='max-w-7xl mx-auto'>
				{/* Header */}
				<div className='flex items-center gap-4 mb-6'>
					<Button variant='ghost' size='sm' onClick={() => navigate('/clients')} className='p-2'>
						<ArrowLeft className='w-4 h-4' />
					</Button>
					<div>
						<h1 className='text-3xl font-bold text-foreground'>Add A Client</h1>
						<p className='text-foreground-muted mt-1'>Fill required details</p>
					</div>
				</div>

				<form onSubmit={handleSubmit} className='space-y-6'>
					{/* Client Information Section */}
					<Card className='p-4'>
						<div className='flex items-center gap-3 mb-4'>
							<Building className='w-5 h-5 text-primary' />
							<h2 className='text-xl font-semibold text-foreground'>Client Information</h2>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
							{/* Basic Information Row */}
							<FloatingInput
								label='Name'
								value={formData.name}
								onChange={value => handleInputChange('name', value)}
								required
								error={!!errors.name}
								errorMessage={errors.name}
							/>

							<FloatingInput
								label='Address 1'
								value={formData.address1}
								onChange={value => handleInputChange('address1', value)}
								required
								error={!!errors.address1}
								errorMessage={errors.address1}
							/>

							<FloatingInput
								label='Address 2'
								value={formData.address2}
								onChange={value => handleInputChange('address2', value)}
							/>

							<FloatingInput
								label='Landmark'
								value={formData.landmark}
								onChange={value => handleInputChange('landmark', value)}
								required
								error={!!errors.landmark}
								errorMessage={errors.landmark}
							/>

							<FloatingInput
								label='Latitude'
								value={formData.latitude}
								onChange={value => handleInputChange('latitude', value)}
								type='number'
								required
								error={!!errors.latitude}
								errorMessage={errors.latitude}
							/>

							<FloatingInput
								label='Longitude'
								value={formData.longitude}
								onChange={value => handleInputChange('longitude', value)}
								type='number'
								required
								error={!!errors.longitude}
								errorMessage={errors.longitude}
							/>

							{/* Location Hierarchy Row */}
							<FloatingDropdown
								label='Select City'
								options={cities}
								value={formData.city}
								onChange={(value: string) => handleInputChange('city', value)}
								loading={citiesLoading}
								required
								error={!!errors.city}
								errorMessage={errors.city}
								disabled={user?.userTypeId ? user.userTypeId > 4 : false}
							/>

							{/* Facility Dropdown - mandatory, filtered by selected city */}
							<FacilityDropdown
								label='Facility'
								value={formData.facility || ''}
								onChange={(value: string) => handleInputChange('facility', value)}
								cityId={formData.city ? parseInt(formData.city) : undefined}
								autoSelectFirst={false}
								className='w-full'
								placeholder='Select Facility'
							/>

							<FloatingDropdown
								label='Select State'
								options={states}
								value={formData.state}
								onChange={(value: string) => handleInputChange('state', value)}
								loading={statesLoading}
								required
								error={!!errors.state}
								errorMessage={errors.state}
								disabled={user?.userTypeId ? user.userTypeId > 4 : false}
							/>

							<FloatingDropdown
								label='Select Country'
								options={countries}
								value={formData.country}
								onChange={(value: string) => handleInputChange('country', value)}
								loading={countriesLoading}
								required
								error={!!errors.country}
								errorMessage={errors.country}
								disabled={user?.userTypeId ? user.userTypeId > 4 : false}
							/>

							{/* Location Type */}
							<FloatingDropdown
								label='Select Location Type'
								options={locationTypes}
								value={formData.locationType}
								onChange={(value: string) => handleInputChange('locationType', value)}
								loading={locationTypesLoading}
								required
								error={!!errors.locationType}
								errorMessage={errors.locationType}
							/>

							<FloatingInput
								label='Zipcode'
								value={formData.zipcode}
								onChange={value => handleInputChange('zipcode', value)}
								required
								error={!!errors.zipcode}
								errorMessage={errors.zipcode}
							/>
						</div>

						{/* On-site Manpower Checkbox */}
						<div className='mt-6'>
							<label className='flex items-center gap-3 cursor-pointer'>
								<input
									type='checkbox'
									checked={formData.onSiteManpower}
									onChange={e => handleInputChange('onSiteManpower', e.target.checked)}
									className='w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary'
								/>
								<span className='text-sm font-medium text-foreground'>On-site Manpower</span>
							</label>
						</div>
					</Card>

					{/* Billing Type Section */}
					<Card className='p-4'>
						<div className='flex items-center gap-3 mb-4'>
							<DollarSign className='w-5 h-5 text-primary' />
							<h2 className='text-xl font-semibold text-foreground'>Billing Type</h2>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
							<FloatingDropdown
								label='Select Billing Type'
								options={billingTypes}
								value={formData.billingType}
								onChange={(value: string) => handleInputChange('billingType', value)}
								loading={billingTypesLoading}
								required
								error={!!errors.billingType}
								errorMessage={errors.billingType}
							/>

							{/* Dynamic Fields based on Billing Type */}
							{formData.billingType === '3' && (
								<>
									<FloatingInput
										label='Fixed Price'
										value={formData.fixedPrice || ''}
										onChange={value => handleInputChange('fixedPrice', value)}
										type='number'
										required
										error={!!errors.fixedPrice}
										errorMessage={errors.fixedPrice}
									/>
									{/* Effective-from month picker — Month + Year dropdowns
									    instead of <input type='month'> because Safari falls back
									    to plain text for that. State stays as 'YYYY-MM-01'. */}
									<FloatingDropdown
										label='Effective Month'
										options={MONTH_OPTIONS.map(m => ({ value: m.value, label: m.label }))}
										value={(() => {
											const m = (formData.fixedPriceEffectiveFrom || '').slice(5, 7);
											return m ? String(parseInt(m, 10)) : '';
										})()}
										onChange={value => {
											const year = (formData.fixedPriceEffectiveFrom || '').slice(0, 4) || String(new Date().getFullYear());
											const mm = String(parseInt(value, 10)).padStart(2, '0');
											handleInputChange('fixedPriceEffectiveFrom', `${year}-${mm}-01`);
										}}
										placeholder='Pick month'
										required
									/>
									<FloatingDropdown
										label='Effective Year'
										options={EFFECTIVE_YEAR_OPTIONS}
										value={(formData.fixedPriceEffectiveFrom || '').slice(0, 4)}
										onChange={value => {
											const mm = (formData.fixedPriceEffectiveFrom || '').slice(5, 7) ||
												String(new Date().getMonth() + 1).padStart(2, '0');
											handleInputChange('fixedPriceEffectiveFrom', `${value}-${mm}-01`);
										}}
										placeholder='Pick year'
										required
									/>
								</>
							)}

							{formData.billingType === '4' && (
								<FloatingDropdown
									label='Billing Sub Type'
									options={billingSubTypes}
									value={formData.billingSubType || ''}
									onChange={(value: string) => handleInputChange('billingSubType', value)}
									loading={billingSubTypesLoading}
									required
									error={!!errors.billingSubType}
									errorMessage={errors.billingSubType}
								/>
							)}
						</div>
					</Card>

					{/* Impact Type Section */}
					<Card className='p-4'>
						<div className='flex items-center gap-3 mb-4'>
							<Target className='w-5 h-5 text-primary' />
							<h2 className='text-xl font-semibold text-foreground'>Impact Types</h2>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
							<MultiSelectDropdown
								label='Select Impact Types'
								options={impactTypes}
								value={formData.impactTypes}
								onChange={(values: string[]) =>
									setFormData(prev => ({ ...prev, impactTypes: values }))
								}
								placeholder=''
								loading={impactTypesLoading}
								required
								error={!!errors.impactTypes}
								errorMessage={errors.impactTypes}
								searchable
								maxDisplayItems={2}
							/>
						</div>
					</Card>

					{/* Submit Button */}
					{/* Submit Button */}
					<div className='flex justify-end'>
						<Button
							type='submit'
							disabled={isSubmitting}
							className='px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors'
						>
							{isSubmitting ? 'Adding Client...' : 'Add Client'}
						</Button>
					</div>
				</form>
			</div>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				message={snackbar.message}
				type={snackbar.type}
				onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
			/>
		</div>
	);
};

export default AddClient;
